import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, query } from '../api/client';
import type { CompanySummary } from '../api/types';
import { IconCompanies, IconPlus, IconSearch } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorNote,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
} from '../components/ui';
import { useCapabilities } from '../lib/auth';
import { formatDate } from '../lib/license';

export function CompaniesPage() {
  const can = useCapabilities();
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['companies', search, includeInactive],
    queryFn: () =>
      api.get<{ companies: CompanySummary[] }>(
        `/api/staff/companies${query({ search, includeInactive })}`,
      ),
  });

  const companies = data?.companies ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Companies</h1>
          <p className="page-head__sub">
            Your customers. A company is what a license attaches to, and what ties an administrator
            to every user they create.
          </p>
        </div>
        {can.canWrite && (
          <div className="page-head__actions">
            <Button variant="primary" icon={<IconPlus />} onClick={() => setCreating(true)}>
              New company
            </Button>
          </div>
        )}
      </div>

      <section className="panel">
        <div className="panel__head">
          <div className="filters grow">
            <div className="search">
              <IconSearch />
              <input
                className="input"
                type="search"
                placeholder="Search name or code"
                value={search}
                aria-label="Search companies"
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
            </div>
            <div className="segmented" role="group" aria-label="Include deactivated companies">
              <button
                type="button"
                aria-pressed={!includeInactive}
                onClick={() => setIncludeInactive(false)}
              >
                Active
              </button>
              <button
                type="button"
                aria-pressed={includeInactive}
                onClick={() => setIncludeInactive(true)}
              >
                All
              </button>
            </div>
          </div>
        </div>

        {isPending && <SkeletonRows />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && companies.length === 0 && (
          <EmptyState
            icon={<IconCompanies size={30} />}
            title={search ? 'No company matches that' : 'No companies yet'}
            body={
              search
                ? 'Try a shorter search, or switch to “All” to include deactivated companies.'
                : 'Add the first customer, then record what they bought and issue their administrator a credential.'
            }
            action={
              search ? (
                <Button onClick={() => setSearch('')}>Clear search</Button>
              ) : can.canWrite ? (
                <Button variant="primary" icon={<IconPlus />} onClick={() => setCreating(true)}>
                  New company
                </Button>
              ) : undefined
            }
          />
        )}

        {companies.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Code</th>
                  <th>Licenses</th>
                  <th>Credentials</th>
                  <th>Contact</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    data-clickable="true"
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong">{company.name}</span>
                        {(company.synced_from?.length ?? 0) > 0 && (
                          <span>
                            <Pill tone="neutral">Synced: {company.synced_from!.join(', ')}</Pill>
                          </span>
                        )}
                        {!company.is_active && <span><Pill tone="expired">Deactivated</Pill></span>}
                      </div>
                    </td>
                    <td className="cell-mono">{company.code}</td>
                    <td>
                      {company.license_count === 0 ? (
                        <span className="cell-meta">none</span>
                      ) : company.usable_license_count === company.license_count ? (
                        <Pill tone="live">
                          {company.license_count} working
                        </Pill>
                      ) : (
                        <Pill tone="expired">
                          {company.license_count - company.usable_license_count} of{' '}
                          {company.license_count} not working
                        </Pill>
                      )}
                    </td>
                    <td className="table__num">{company.active_user_count}</td>
                    <td className="cell-meta">{company.contact_email ?? '—'}</td>
                    <td className="cell-meta tabular">{formatDate(company.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CreateCompanyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => navigate(`/companies/${id}`)}
      />
    </>
  );
}

function CreateCompanyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setName('');
    setCode('');
    setCodeEdited(false);
    setContactPerson('');
    setContactEmail('');
    setNotes('');
  };

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ company: { id: string; name: string } }>('/api/staff/companies', {
        name,
        code,
        contactPerson: contactPerson || undefined,
        contactEmail: contactEmail || undefined,
        notes: notes || undefined,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      push({ tone: 'success', title: `${data.company.name} added` });
      reset();
      onClose();
      onCreated(data.company.id);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        mutation.reset();
        onClose();
      }}
      title="New company"
      hint="The code becomes the username prefix you give their people — acme.admin, acme.bob."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <ErrorNote error={mutation.error} />}

        <TextField
          label="Company name"
          value={name}
          required
          autoFocus
          onChange={(event) => {
            const value = event.currentTarget.value;
            setName(value);
            // Suggest a code from the name until the user takes it over.
            if (!codeEdited) setCode(slugify(value));
          }}
        />

        <TextField
          label="Code"
          value={code}
          required
          mono
          hint="Lowercase letters, digits and hyphens."
          onChange={(event) => {
            setCodeEdited(true);
            setCode(event.currentTarget.value);
          }}
        />

        <div className="form-grid form-grid--2">
          <TextField
            label="Contact person"
            optional
            value={contactPerson}
            onChange={(event) => setContactPerson(event.currentTarget.value)}
          />
          <TextField
            label="Contact email"
            type="email"
            optional
            value={contactEmail}
            onChange={(event) => setContactEmail(event.currentTarget.value)}
          />
        </div>

        <TextAreaField
          label="Notes"
          optional
          value={notes}
          hint="Anything the next person on support should know."
          onChange={(event) => setNotes(event.currentTarget.value)}
        />

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Add company
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}
